import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  apiService,
  sessionService,
  User,
  Company,
  AuthResponse,
} from "../services/api";
import { analytics, AnalyticsEvent } from "../services/analytics";
import {
  PRODUCTS_CACHE_KEY,
} from "../auth/companyProductsCache";
import { clearAppSessionState } from "../auth/clearAppSessionState";
import { syncCompanyProducts } from "../auth/syncCompanyProducts";
import { ROUTES_NAMES } from "../components/routes";
import { isPodcastInvitePath, isPublicSharePath } from "../utils/invitePaths";
import {
  resolveEffectiveUserProducts,
  getDefaultRouteForRoleAndProducts,
  userHasProductAccess,
  userHasExplorerTabAccess,
  userHasAnyExplorerAccess,
} from "../auth/permissions";
import {
  coalesceDateOverride,
  coalesceSponsorCategoryOverride,
} from "../auth/userFilterOverrides";
import { useAuthSyncQuery } from "../auth/useAuthSyncQuery";
import { chileanTodayYmd } from "../miContenido/metricsReportPeriod";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCompanyAdmin: boolean;
  isLoading: boolean;
  isProductPermissionsReady: boolean;
  companyProducts: string[];
  hasProductAccess: (product: string) => boolean;
  hasExplorerTabAccess: (tabIndex: number) => boolean;
  hasAnyExplorerAccess: () => boolean;
  getDateFilterMinStartDate: () => string;
  getDateFilterMaxEndDate: () => string;
  getMiContenidoDateFilterMinStartDate: () => string;
  getMiContenidoDateFilterMaxEndDate: () => string;
  getCompanyTrackerBrandId: () => string | null;
  getExplorerSponsorCategoryFilter: () => string | null;
  getDefaultRoute: () => string;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithDemoToken: (token: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name: string,
    company?: string,
  ) => Promise<void>;
  establishSessionFromAuthResponse: (
    response: AuthResponse & { alreadyMember?: boolean },
  ) => Promise<void>;
  signOut: () => void;
  refreshCompanyProducts: () => Promise<void>;
  company: Company | null;
  isCompanyLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const authSync = useAuthSyncQuery(user);

  const identifyUser = (u: User) => {
    const normalizedEmail = u.email?.trim().toLowerCase() ?? "";
    const amplitudeUserId = normalizedEmail || u._id;
    analytics.identify(amplitudeUserId, {
      email: normalizedEmail || u.email || undefined,
      podscopeUserId: u._id,
      name: u.name,
      role: u.role,
      company: u.company,
      companyId: u.companyId,
      companyRole: u.companyRole,
    });
  };

  const effectiveUser = authSync.data?.user ?? user;
  const companyData = authSync.data?.company ?? null;

  const companyProducts: string[] = companyData?.products ?? [];
  const enablePerUserPermissions: boolean =
    companyData?.enablePerUserProductPermissions ?? false;
  const companyDateFilterMinStartDate: string | null =
    companyData?.dateFilterMinStartDate ?? null;
  const companyDateFilterMaxEndDate: string | null =
    companyData?.dateFilterMaxEndDate ?? null;
  const companyMiContenidoDateFilterMinStartDate: string | null =
    companyData?.miContenidoDateFilterMinStartDate ?? null;
  const companyMiContenidoDateFilterMaxEndDate: string | null =
    companyData?.miContenidoDateFilterMaxEndDate ?? null;
  const companyTrackerBrandId: string | null =
    companyData?.trackerBrandId ?? null;
  const companyExplorerSponsorCategoryFilter: string | null =
    companyData?.explorerSponsorCategoryFilter ?? null;

  const accessibleProducts = resolveEffectiveUserProducts(
    enablePerUserPermissions,
    effectiveUser?.userProducts,
    companyProducts,
  );

  const isProductPermissionsReady =
    !user?.companyId ||
    authSync.status === "success" ||
    authSync.status === "error";

  useEffect(() => {
    const failSafeId = window.setTimeout(() => setIsLoading(false), 25_000);

    const init = async () => {
      if (
        isPodcastInvitePath(window.location.pathname) ||
        isPublicSharePath(window.location.pathname)
      ) {
        return;
      }

      try {
        const { user: stored } = sessionService.getSession();
        if (!stored) return;

        if (!(await apiService.validateSession())) {
          sessionService.clearSession();
          return;
        }

        const [, currentUser] = await Promise.all([
          syncCompanyProducts(queryClient, stored.companyId, false),
          apiService.getCurrentUser().catch((err) => {
            console.error("Error refreshing user on init:", err);
            return stored;
          }),
        ]);

        queryClient.setQueryData<User>(
          ["current-user", currentUser._id],
          currentUser,
        );
        setUser(currentUser);
        identifyUser(currentUser);
      } catch (e) {
        console.error("Error initializing auth:", e);
        sessionService.clearSession();
      } finally {
        window.clearTimeout(failSafeId);
        setIsLoading(false);
      }
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === PRODUCTS_CACHE_KEY && e.newValue) {
        void queryClient.invalidateQueries({ queryKey: ["auth-sync"] });
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [user, queryClient]);

  const signIn = (email: string, password: string) =>
    completeSignIn(() => apiService.signIn(email, password));

  const signInWithDemoToken = (token: string) =>
    completeSignIn(() => apiService.demoLogin(token), "demo_link");

  const completeSignIn = async (
    authenticate: () => Promise<AuthResponse>,
    source?: string,
  ) => {
    try {
      clearAppSessionState(queryClient);
      const response = await authenticate();
      sessionService.setSession(response.user, response.token);

      await syncCompanyProducts(queryClient, response.user.companyId, true);

      queryClient.setQueryData<User>(
        ["current-user", response.user._id],
        response.user,
      );
      setUser(response.user);
      identifyUser(response.user);
      analytics.track(AnalyticsEvent.USER_SIGNIN, {
        email: response.user.email,
        companyId: response.user.companyId,
        ...(source ? { source } : {}),
      });

      navigate(ROUTES_NAMES.ROOT, { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      if (err.response?.data?.message) {
        throw new Error(err.response.data.message);
      }
      throw new Error("Error al iniciar sesión");
    }
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    company?: string,
  ) => {
    try {
      clearAppSessionState(queryClient);
      const response = await apiService.signUp(name, email, password, company);
      sessionService.setSession(response.user, response.token);

      await syncCompanyProducts(queryClient, response.user.companyId, true);

      queryClient.setQueryData<User>(
        ["current-user", response.user._id],
        response.user,
      );
      setUser(response.user);
      identifyUser(response.user);
      analytics.track(AnalyticsEvent.USER_SIGNUP, {
        email: response.user.email,
        companyId: response.user.companyId,
      });

      navigate(ROUTES_NAMES.ROOT, { replace: true });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      if (err.response?.data?.message) {
        throw new Error(err.response.data.message);
      }
      throw new Error("Error al crear la cuenta");
    }
  };

  const establishSessionFromAuthResponse = async (
    response: AuthResponse & { alreadyMember?: boolean },
  ) => {
    if (!response.token || !response.user) {
      throw new Error("Sesión inválida");
    }
    clearAppSessionState(queryClient);
    sessionService.setSession(response.user, response.token);
    await syncCompanyProducts(queryClient, response.user.companyId, true);
    queryClient.setQueryData<User>(
      ["current-user", response.user._id],
      response.user,
    );
    setUser(response.user);
    identifyUser(response.user);

    if (response.alreadyMember) {
      analytics.track(AnalyticsEvent.USER_SIGNIN, {
        email: response.user.email,
        companyId: response.user.companyId,
        source: "invite",
      });
    } else {
      analytics.track(AnalyticsEvent.USER_SIGNUP, {
        email: response.user.email,
        companyId: response.user.companyId,
        source: "invite",
      });
    }

    navigate(ROUTES_NAMES.ROOT, { replace: true });
  };

  const signOut = () => {
    analytics.track(AnalyticsEvent.USER_SIGNOUT);
    analytics.reset();
    setUser(null);
    clearAppSessionState(queryClient);
    sessionService.clearSession();
    navigate("/auth");
  };

  const refreshCompanyProducts = async () => {
    await authSync.refetch();
  };

  const getDateFilterMinStartDate = (): string => {
    if (effectiveUser?.role === "admin") return "2025-01-01";
    return (
      coalesceDateOverride(
        effectiveUser?.dateFilterMinStartDate,
        companyDateFilterMinStartDate,
      ) || "2026-01-01"
    );
  };

  const getDateFilterMaxEndDate = (): string => {
    const chileToday = chileanTodayYmd();
    if (effectiveUser?.role === "admin") return chileToday;
    return (
      coalesceDateOverride(
        effectiveUser?.dateFilterMaxEndDate,
        companyDateFilterMaxEndDate,
      ) || chileToday
    );
  };

  const getMiContenidoDateFilterMinStartDate = (): string => {
    if (effectiveUser?.role === "admin") return "2025-01-01";
    return (
      coalesceDateOverride(
        effectiveUser?.miContenidoDateFilterMinStartDate,
        companyMiContenidoDateFilterMinStartDate,
      ) || "2026-01-01"
    );
  };

  const getMiContenidoDateFilterMaxEndDate = (): string => {
    const chileToday = chileanTodayYmd();
    if (effectiveUser?.role === "admin") return chileToday;
    return (
      coalesceDateOverride(
        effectiveUser?.miContenidoDateFilterMaxEndDate,
        companyMiContenidoDateFilterMaxEndDate,
      ) || chileToday
    );
  };

  const value: AuthContextType = {
    user: effectiveUser,
    isAuthenticated: !!effectiveUser,
    isAdmin: effectiveUser?.role === "admin",
    isCompanyAdmin: effectiveUser?.companyRole === "admin",
    isLoading,
    isProductPermissionsReady,
    companyProducts,
    hasProductAccess: (product: string) =>
      userHasProductAccess(effectiveUser, accessibleProducts, product),
    hasExplorerTabAccess: (tabIndex: number) =>
      userHasExplorerTabAccess(effectiveUser, accessibleProducts, tabIndex),
    hasAnyExplorerAccess: () =>
      userHasAnyExplorerAccess(effectiveUser, accessibleProducts),
    getDateFilterMinStartDate,
    getDateFilterMaxEndDate,
    getMiContenidoDateFilterMinStartDate,
    getMiContenidoDateFilterMaxEndDate,
    getCompanyTrackerBrandId: () => companyTrackerBrandId,
    getExplorerSponsorCategoryFilter: () => {
      if (effectiveUser?.role === "admin") return null;
      return coalesceSponsorCategoryOverride(
        effectiveUser?.explorerSponsorCategoryFilter,
        companyExplorerSponsorCategoryFilter,
      );
    },
    getDefaultRoute: () =>
      getDefaultRouteForRoleAndProducts(
        effectiveUser?.role,
        resolveEffectiveUserProducts(
          enablePerUserPermissions,
          effectiveUser?.userProducts,
          companyProducts,
        ),
      ),
    signIn,
    signInWithDemoToken,
    signUp,
    establishSessionFromAuthResponse,
    signOut,
    refreshCompanyProducts,
    company: companyData,
    isCompanyLoading: Boolean(
      user?.companyId && authSync.isPending && !authSync.data,
    ),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
